import { generateObjectId } from "../../../utils/objectIdUtils";

const unit = 4;
const spacing = 2 * unit;
const bevel = 2 * unit;

const createAndRendererState = (state, joint, graph, dia, elementTools, paper, activitiesObject, initBlock = []) => {
  let state_name_is = state?.name || "";
  let widthIs = state.w;
  let activitySpacing = 30; // Space between activities
  let startY = state.y + 50;

  let stateElement = new joint.shapes.standard.HeaderedRectangle({
    position: { x: state.x, y: state.y },
    size: { width: widthIs, height: 80 },
    z: 2
  });

  stateElement.attr("root/title", state.name);
  stateElement.attr("header/fill", "#dadada");
  stateElement.attr("headerText/text", state_name_is);

  stateElement.attr({
    header: {
      //fill: "#97bfff",
      fill: state.state_type === "start" ? "#ace9b7" : state.state_type === "end" ? "#f5adad" : "#97bfff",
      stroke: state.state_type === "start" ? "green" : state.state_type === "end" ? "red" : "black",
      strokeWidth: 0,
      height: 35,
      y: 0
    },
    body: {
      //fill: state.state_type === "start" ? "#edf9ef" : state.state_type === "end" ? "#f8e2e2" : "white",
      fill: "white",
      stroke: state.state_type === "start" ? "green" : state.state_type === "end" ? "red" : "black",
      strokeWidth: 0,
      filter: "drop-shadow(3px 5px 5px rgba(0, 0, 0, 0.4))",
      rx: 10,
      ry: 10
    },
    label: {
      fontSize: 12
    },

    headerText: {
      refY: 18, // Center the text vertically in the header
      fontSize: 17,
      "font-weight": "Medium",
      textAnchor: "middle", // Center text horizontally
      textVerticalAnchor: "middle" // Vertically center text
    },
    eltype: "state"
  });

  stateElement.attr("stateid", state.id);

  stateElement.addTo(graph);
  const connectTool = new dia.ToolsView({
    tools: [new elementTools.HoverConnect()]
  });
  stateElement.findView(paper).addTools(connectTool);

  let lastActivityY = startY;

  state.activities.forEach((activity, index) => {
    //add all allowed activities for the state
    let nameIs = activitiesObject?.[activity]?.name || "";

    if (nameIs.length > 30) {
      nameIs = nameIs.substring(0, 30);
      nameIs += "...";
    }

    let activityRect = new joint.shapes.standard.Rectangle({
      position: { x: state.x + 10, y: startY + index * activitySpacing },
      size: { width: widthIs, height: 24 },
      z: 10
    });

    activityRect.attr({
      body: {
        fill: "#e0e0e0",
        rx: 5,
        ry: 5,
        strokeWidth: 0
      },
      label: {
        text: nameIs,
        fontSize: 14,
        title: activitiesObject?.[activity]?.name,
        refX: 10,
        refY: 9,
        textAnchor: "start",
        dominantBaseline: "middle"
      },
      eltype: "activity"
    });

    activityRect.attr("activityid", activity);
    activityRect.addTo(graph);

    const jobsCount = activitiesObject?.[activity]?.jobs_trigger?.length;

    if (jobsCount > 0) {
      const bulletColor = jobsCount > 0 ? "#c9d7ed" : "none";

      // Create the bullet rectangle (small rectangle representing the bullet)
      let bulletRect = new joint.shapes.standard.Rectangle({
        position: { x: state.x + 10 + widthIs + 5, y: state.y + 50 + index * 30 }, // Place it at the end of the activityRect
        size: { width: 20, height: 24 },
        attrs: {
          body: {
            fill: bulletColor, // Set color dynamically
            strokeWidth: 0,
            rx: 2,
            ry: 2
          },
          label: {
            text: jobsCount > 0 ? jobsCount : "", // Show jobsCount inside the bulletRect
            fontSize: 12,
            fill: "#454545", // Set the text color for contrast
            textAnchor: "middle", // Center the text horizontally
            dominantBaseline: "middle" // Center the text vertically
          },
          eltype: "PAJobs"
        }
      });
      bulletRect.attr("activityid", activity);
      bulletRect.addTo(graph);
      stateElement.embed(bulletRect);
    }

    // Embed activity rectangle and bullet rectangle inside the state element
    stateElement.embed(activityRect);
    activityRect.fitParent({
      padding: { top: 50, bottom: 10, right: 10, left: 10 } // Adjust the right padding as needed
    });

    lastActivityY = startY + index * activitySpacing;
  }); //foreach activity

  if (state.state_type !== "end") {
    if (state.activities.length === 0) {
      lastActivityY = lastActivityY - 35;
    }

    // Add "+ Activity" button as a circular shape
    let addActivityCircle = new joint.shapes.standard.Circle({
      position: { x: state.x + 15, y: lastActivityY + activitySpacing }, // Centered horizontally
      size: { width: 30, height: 30 }, // Circular size
      z: 10
    });

    addActivityCircle.attr({
      body: {
        fill: "#e0e0e0",
        stroke: "#e0e0e0",
        strokeWidth: 1,
        cursor: "pointer"
      },
      label: {
        text: "+",
        fontSize: 18,
        title: "Add Activity",
        fill: "#000000",
        textAnchor: "middle",
        refX: "50%",
        refY: "40%",
        dominantBaseline: "middle"
      },
      eltype: "add_activity"
    });

    addActivityCircle.attr("stateid", state.id);
    addActivityCircle.attr("statename", state.name);
    addActivityCircle.addTo(graph);
    stateElement.embed(addActivityCircle);
  }

  // Create init circles for start states (returned separately to be registered in gStateElements)
  const initElements = [];
  if (state.state_type === "start" && state.init_activity_ids) {
    for (let i = 0; i < state.init_activity_ids?.length; i++) {
      const init_activity_id = state.init_activity_ids[i];
      const initId = `init_${init_activity_id}`;

      // Check if we have stored position for this init round
      const storedInit = initBlock.find((item) => item.id === initId);

      // Use stored position if available, otherwise calculate default position
      let posX = storedInit ? storedInit.x : state.x - 160;
      let posH = storedInit ? storedInit.y : state.y + i * 100;

      let round = new joint.shapes.basic.Circle({
        position: { x: posX, y: posH },
        size: { width: 80, height: 80 },
        attrs: {
          circle: {
            fill: "#97bfff", // Background color
            stroke: "green", // Border color
            "stroke-width": 0,
            filter: "drop-shadow(5px 5px 5px rgba(0, 0, 0, 0.3))"
          },
          text: {
            text: "Init",
            fill: "black", // Text color
            "font-size": 15,
            "font-weight": "bold",
            "text-anchor": "middle",
            "ref-x": 0.5,
            "ref-y": 0.5,
            "y-alignment": "middle"
          },
          eltype: "init_activity"
        }
      });

      // Set a unique ID for this init element that matches our virtual source pattern
      round.set("id", initId);
      round.attr("root/title", activitiesObject[init_activity_id]?.name);
      round.attr("activityid", init_activity_id);
      // Store the position x and y for later retrieval
      round.attr("positionX", posX);
      round.attr("positionY", posH);
      round.addTo(graph);

      // Add HoverConnect tool to enable connection magnets on hover
      const initConnectTool = new dia.ToolsView({
        tools: [new elementTools.HoverConnect()]
      });
      round.findView(paper).addTools(initConnectTool);

      // Store the init element to be registered in gStateElements
      initElements.push({ id: `init_${init_activity_id}`, element: round });
    }
  }

  return { stateElement, initElements };
};

const createAndRenderGateway = (gateway, joint, graph, dia, elementTools, paper) => {
  const gatewayElement = new joint.shapes.standard.Polygon({
    position: { x: gateway.x, y: gateway.y },
    size: { width: gateway.w, height: gateway.h },
    attrs: {
      body: {
        refPoints: "0,10 10,0 20,10 10,20",
        fill: "rgba(255, 223, 0, 0.6)"
      },
      label: {
        text: gateway?.name,
        fill: "#000000",
        fontSize: 16,
        fontWeight: "Medium",
        refY: "50%",
        refY2: "0%",
        textVerticalAnchor: "middle",
        textAnchor: "middle",
        eltype: "gateway",
        magnet: false
      }
    }
  });

  gatewayElement.attr({
    eltype: "gateway",
    gatewayId: gateway.id
  });

  gatewayElement.addTo(graph);

  const connectTool = new dia.ToolsView({
    tools: [new elementTools.HoverConnect()]
  });

  gatewayElement.findView(paper).addTools(connectTool);

  return gatewayElement;
};

const createAndRendererTransition = (transition, gStateElements, graph, joint) => {
  let sourceState = gStateElements[transition.source];
  let targetState = gStateElements[transition.target];
  let link = createAndRendererFlow(sourceState, targetState, transition.anchors, joint);
  if (!link) return;

  // Create a single label with multiple lines using `tspan`
  let activitiesText = transition?.activities?.map((activity) => `▪ ${activity.name}`).join("\n");
  let gatewayText = transition?.gateways?.map((activity) => `${activity.name}`).join("\n");

  if (gatewayText) {
    link.appendLabel({
      attrs: {
        labelText: {
          text: gatewayText,
          fontWeight: "normal",
          fontSize: 12,
          textVerticalAnchor: "top",
          lineHeight: 18,
          fill: "black"
        },
        labelBody: {
          fill: "#fffacd",
          stroke: "#333",
          "stroke-width": 1,
          rx: 5,
          ry: 5
        }
      }
    });
  } else if (activitiesText) {
    link.appendLabel({
      attrs: {
        text: {
          text: activitiesText,
          fontWeight: "normal",
          fontSize: 12,
          textVerticalAnchor: "top",
          lineHeight: 18
        }
      }
    });
  }

  // Ensure color and dashed line for empty activities
  link.attr({
    tid: transition.tid,
    line: {
      stroke: transition.border_color === "black" ? "#454545" : transition.border_color,
      strokeWidth: transition.stroke_width,
      "stroke-dasharray": transition.activities?.length === 0 ? "5,5" : "none"
    }
  });

  if (transition?.gateways) {
    link.attr({
      tid: transition.tid,
      line: {
        stroke: transition.border_color === "black" ? "#454545" : transition.border_color,
        strokeWidth: transition.stroke_width,
        "stroke-dasharray": transition?.gateways?.length === 0 ? "5,5" : "none"
      }
    });
  }

  link.addTo(graph);
};

const createAndRendererFlow = (source, target, anchors, joint) => {
  if (!source || !target) {
    return;
  }
  if (!anchors) {
    anchors = { source: { name: "midSide" }, target: { name: "midSide" } };
  } else {
    if (!anchors.source.name) anchors.source.name = "midSide";
    if (!anchors.target.name) anchors.target.name = "midSide";
  }
  return new joint.shapes.standard.Link({
    source: { id: source.id, anchor: anchors.source },
    target: { id: target.id, anchor: anchors.target },
    markup: [
      {
        tagName: "path",
        selector: "wrapper",
        attributes: {
          fill: "none",
          cursor: "pointer",
          stroke: "transparent",
          "stroke-linecap": "round"
        }
      },
      {
        tagName: "path",
        selector: "outline",
        attributes: {
          fill: "none",
          "pointer-events": "none"
        }
      },
      {
        tagName: "path",
        selector: "line",
        attributes: {
          fill: "none",
          "pointer-events": "none"
        }
      }
    ],
    defaultLabel: {
      attrs: {
        labelBody: {
          fill: "#454545",
          ref: "labelText",
          d: `
                  M calc(x-${spacing}) calc(y-${spacing})
                  m 0 ${bevel} l ${bevel} -${bevel}
                  h calc(w+${2 * (spacing - bevel)}) l ${bevel} ${bevel}
                  v calc(h+${2 * (spacing - bevel)}) l -${bevel} ${bevel}
                  H calc(x-${spacing - bevel}) l -${bevel} -${bevel} Z
              `
        },
        labelText: {
          textAnchor: "middle",
          textVerticalAnchor: "middle",
          fontStyle: "italic",
          fill: "white"
        }
      },
      markup: [
        {
          tagName: "path",
          selector: "labelBody"
        },
        {
          tagName: "text",
          selector: "labelText"
        }
      ]
    }
  });
};

const getCanvasHeightValue = (array) => {
  let heightValue,
    maxHeightValue = 0,
    yPosValue = 0;
  for (let index = 0; index < array.length; index++) {
    const element = array[index];
    if (element.y > yPosValue) {
      yPosValue = element.y;
      // maxHeightValue = element.height;
      maxHeightValue = 50;
    }
  }

  heightValue = maxHeightValue + yPosValue + 50;

  return heightValue;
};

const getInsertConfig = (type, name) => {
  const date = new Date();
  if (type === "activity") {
    return {
      endpoint: `activities`,
      config: {
        id: generateObjectId(),
        name: name,
        form: {
          name: name,
          sections: [
            {
              fields: [],
              key: "Section_123",
              progress_bar_title: "S1",
              name: "Section 1"
            }
          ],
          settings: {
            isMultiple: false,
            progress_bar: true,
            progress_bar_clickable: true,
            progress_bar_color_codes: {
              empty: "#808080",
              filled_valid: "#4FB32C",
              filled_invalid: "#E83429",
              filled_incomplete: "#F9D949"
            }
          },
          master_data: {}
        },
        state_change_logic: [
          {
            state_id: "",
            condition: ""
          }
        ],
        activity_owner_assignment: {
          groups: [],
          designations: [],
          type: "anyone",
          multiselect: false,
          copy_parent_rule: false,
          can_reassign: false
        },
        jobs_trigger: [],
        created_at: date.toISOString(),
        modified_at: date.toISOString(),
        type: "default",
        can_delete: true
      }
    };
  } else if (type === "state") {
    return {
      endpoint: `states`,
      config: {
        id: generateObjectId(),
        name: name,
        remember: {
          allow_state_targets: [],
          deny_states_targets: [],
          allow_state_sources: [],
          deny_states_sources: [],
          allow_activities: [],
          deny_activities: [],
          source_activity: [],
          allow_target_gateways: []
        },
        type: "intermediate",
        created_at: date.toISOString(),
        modified_at: date.toISOString()
      }
    };
  } else if (type === "statemachine") {
    return {
      endpoint: "",
      config: {
        allow_instance_owner: true,
        name: name,
        sm_type: "default"
      }
    };
  } else if (type === "gateway") {
    return {
      endpoint: `gateways`,
      config: {
        id: generateObjectId(),
        name: name,
        type: "gateway",
        allow_state_targets: [],
        logic_type: "",
        state_change_logic: [],
        procode_logic: {
          procode_id: "",
          state_label_mapping: []
        },
        created_at: date.toISOString(),
        modified_at: date.toISOString()
      }
    };
  }
};

const removeStateKeyFromSM = async (key, smData) => {
  let rememberItemsArray = [
    "allow_state_targets",
    "deny_states_targets",
    "allow_state_sources",
    "deny_states_sources",
    "allow_activities",
    "deny_activities",
    "source_activity"
  ];
  for (let index = 0; index < smData.states.length; index++) {
    let statesRemember = smData.states[index].remember;
    for (let i = 0; i < rememberItemsArray.length; i++) {
      let element = statesRemember[rememberItemsArray[i]];
      if (element.includes(key)) {
        element.splice(element.indexOf(key), 1);
      }
    }
  }
};

const getPaperclientWidthandclientHeight = (statesArray = []) => {
  let width = 0,
    height = 0;

  for (let index = 0; index < statesArray.length; index++) {
    const element = statesArray[index];
    if (element.x > width) {
      width = element.x;
    }
    if (element.y > height) {
      height = element.y;
    }
  }

  return {
    width: width + 250,
    height: height + 250
  };
};

export {
  createAndRendererState,
  createAndRendererTransition,
  getCanvasHeightValue,
  getInsertConfig,
  getPaperclientWidthandclientHeight,
  removeStateKeyFromSM,
  createAndRenderGateway
};
